import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import {
  OrderItemApi,
  OrderItemProofApi,
  type OrderItemUpdateDeliveryNotesCommand,
} from "@/config/apiClient";
import { createApiClient } from "@/config/createApiClient";
import { useWithTrackingMutation } from "@/hooks/queries/useWithTrackingMutation";
import { selectedClientServiceIdAtom } from "@/store";

const apiClient = createApiClient(OrderItemApi);
const orderItemProofApiClient = createApiClient(OrderItemProofApi);

export const useOrderItems = () => {
  const selectedClientServiceIdAtomValue = useAtomValue(
    selectedClientServiceIdAtom
  );

  const queryClient = useQueryClient();

  const useGetOrderItem = ({
    orderId,
    orderItemId,
  }: {
    orderId: string;
    orderItemId: string;
  }) =>
    useQuery({
      queryKey: ["orderItem", orderId, orderItemId],
      enabled: selectedClientServiceIdAtomValue > 0,

      queryFn: () =>
        apiClient.orderItemsControllerGetOrderItem({
          clientServiceId: selectedClientServiceIdAtomValue,
          orderId,
          orderItemId,
        }),
    });

  const useAddOrderItemProof = () =>
    useMutation({
      mutationKey: ["addOrderItemProof"],
      mutationFn: (params: {
        orderId: string;
        orderItemId: string;
        orderItemProofAddCommand: Parameters<
          typeof orderItemProofApiClient.orderItemProofsControllerAddOrderItemProof
        >[0]["orderItemProofAddCommand"];
      }) =>
        orderItemProofApiClient.orderItemProofsControllerAddOrderItemProof({
          clientServiceId: selectedClientServiceIdAtomValue,
          orderId: params.orderId,
          orderItemId: params.orderItemId,
          orderItemProofAddCommand: params.orderItemProofAddCommand,
        }),
      onSuccess: async (_, params) => {
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          queryClient.refetchQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
        ]);
      },
    });

  const useUpdateOrderItemsDeliveryNote = () =>
    useWithTrackingMutation<
      {
        orderId: string;
        orderItemId: string;
        clientServiceId: number;
        orderItemUpdateDeliveryNotesCommand: OrderItemUpdateDeliveryNotesCommand;
      },
      Awaited<
        ReturnType<
          typeof apiClient.orderItemsControllerUpdateOrderItemsDeliveryNotes
        >
      >
    >(
      (params) =>
        apiClient.orderItemsControllerUpdateOrderItemsDeliveryNotes({
          clientServiceId: selectedClientServiceIdAtomValue,
          orderId: params.orderId,
          orderItemUpdateDeliveryNotesCommand:
            params.orderItemUpdateDeliveryNotesCommand,
        }),
      [
        {
          name: "order_item_update_delivery_notes",
          getMetaData: (_res, _data) => ({
            order_id: _data.orderId,
            is_final_delivery_note:
              _data.orderItemUpdateDeliveryNotesCommand.isFinalDeliveryNote ??
              null,
          }),
        },
      ],
      {
        mutationKey: ["orderItemUpdateDeliveryNotes"],
        onSuccess: (_, params) =>
          Promise.all([
            queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
            queryClient.refetchQueries({
              queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
            }),
            queryClient.refetchQueries({
              queryKey: ["orderItem", params.orderId, params.orderItemId],
            }),
            queryClient.invalidateQueries({ queryKey: ["worklist"] }),
            queryClient.invalidateQueries({ queryKey: ["documents"] }),
          ]),
      }
    );

  const useCancelOrderItem = () =>
    useMutation({
      mutationFn: (params: { orderItemId: string; orderId: string }) =>
        apiClient.orderItemsControllerCancelOrderItem({
          clientServiceId: selectedClientServiceIdAtomValue,
          orderId: params.orderId,
          orderItemId: params.orderItemId,
        }),
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ["orders"] }),
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          queryClient.invalidateQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["activeOrders"] }),
        ]),
    });

  return {
    useGetOrderItem,
    useAddOrderItemProof,
    useUpdateOrderItemsDeliveryNote,
    useCancelOrderItem,
  };
};
